import React from 'react'
import { useTranslation } from 'react-i18next'
import { motion } from 'framer-motion'
import { ArrowDown, ArrowUp, Zap, Activity } from 'lucide-react'
import { SpeedTestResult } from './SpeedTest'

interface TestResultsProps {
  results: SpeedTestResult
}

const TestResults: React.FC<TestResultsProps> = ({ results }) => {
  const { t } = useTranslation()

  const formatSpeed = (speed: number) => speed.toFixed(1)
  const formatPing = (ping: number) => Math.round(ping)

  const getSpeedColor = (speed: number, type: 'download' | 'upload') => {
    const threshold = type === 'download' ? 50 : 25
    if (speed >= threshold) return 'text-apple-green'
    if (speed >= threshold / 2) return 'text-apple-orange'
    return 'text-apple-red'
  }

  const getPingColor = (ping: number) => {
    if (ping <= 20) return 'text-apple-green'
    if (ping <= 50) return 'text-apple-orange'
    return 'text-apple-red'
  }

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  }

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { 
      opacity: 1, 
      y: 0,
      transition: {
        duration: 0.4,
        ease: [0.25, 0.1, 0.25, 1]
      }
    }
  }

  return (
    <motion.div 
      className="grid grid-cols-2 md:grid-cols-4 gap-4"
      variants={containerVariants}
      initial="hidden"
      animate="visible"
    >
      {/* Download Speed */}
      <motion.div 
        className="stat-card group"
        variants={itemVariants}
      >
        <div className="flex items-center justify-center mb-3">
          <div className="flex items-center justify-center w-10 h-10 bg-apple-blue/10 rounded-full group-hover:bg-apple-blue/20 transition-colors">
            <ArrowDown className="w-5 h-5 text-apple-blue" />
          </div>
        </div>
        <div className={`text-3xl font-semibold num-display ${getSpeedColor(results.downloadSpeed, 'download')}`}>
          {formatSpeed(results.downloadSpeed)}
        </div>
        <div className="text-sm text-apple-gray-500 mt-1">{t('mbps')}</div>
        <div className="text-xs text-apple-gray-400 mt-2">{t('downloadSpeed')}</div>
      </motion.div>

      {/* Upload Speed */}
      <motion.div 
        className="stat-card group"
        variants={itemVariants}
      >
        <div className="flex items-center justify-center mb-3">
          <div className="flex items-center justify-center w-10 h-10 bg-apple-green/10 rounded-full group-hover:bg-apple-green/20 transition-colors">
            <ArrowUp className="w-5 h-5 text-apple-green" />
          </div>
        </div>
        <div className={`text-3xl font-semibold num-display ${getSpeedColor(results.uploadSpeed, 'upload')}`}>
          {formatSpeed(results.uploadSpeed)}
        </div>
        <div className="text-sm text-apple-gray-500 mt-1">{t('mbps')}</div>
        <div className="text-xs text-apple-gray-400 mt-2">{t('uploadSpeed')}</div>
      </motion.div>

      {/* Ping */}
      <motion.div 
        className="stat-card group"
        variants={itemVariants}
      >
        <div className="flex items-center justify-center mb-3">
          <div className="flex items-center justify-center w-10 h-10 bg-apple-orange/10 rounded-full group-hover:bg-apple-orange/20 transition-colors">
            <Zap className="w-5 h-5 text-apple-orange" />
          </div>
        </div>
        <div className={`text-3xl font-semibold num-display ${getPingColor(results.ping)}`}>
          {formatPing(results.ping)}
        </div>
        <div className="text-sm text-apple-gray-500 mt-1">{t('ms')}</div>
        <div className="text-xs text-apple-gray-400 mt-2">{t('ping')}</div>
      </motion.div>

      {/* Jitter */}
      <motion.div 
        className="stat-card group"
        variants={itemVariants}
      >
        <div className="flex items-center justify-center mb-3">
          <div className="flex items-center justify-center w-10 h-10 bg-apple-purple/10 rounded-full group-hover:bg-apple-purple/20 transition-colors">
            <Activity className="w-5 h-5 text-apple-purple" />
          </div>
        </div>
        <div className={`text-3xl font-semibold num-display ${getPingColor(results.jitter)}`}>
          {formatPing(results.jitter)}
        </div>
        <div className="text-sm text-apple-gray-500 mt-1">{t('ms')}</div>
        <div className="text-xs text-apple-gray-400 mt-2">{t('jitter')}</div>
      </motion.div>
    </motion.div>
  )
}

export default TestResults
